// src/modules/quantum/QMLLab.jsx
// QML Lab — quantum kernels · variational circuits · quantum advantage · NISQ · data encoding

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#4ade80';
const P  = ['#4ade80','#22d3ee','#a78bfa','#fbbf24','#f87171','#fb923c'];
const PI = Math.PI;

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── MATH UTILS ───────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*PI*v); };
const sigmoid = x => 1/(1+Math.exp(-x));
const dot = (a,b) => a.reduce((s,v,i)=>s+v*b[i],0);

// Quantum kernel: k(x,y) = |⟨φ(x)|φ(y)⟩|²
// φ(x) encodes x as rotation angles on Bloch sphere
const quantumKernel = (x, y, nQubits=2) => {
  // Feature map: Rx(πx₀)·Rz(πx₀x₁) on each qubit
  // Simplified: use cosine similarity with nonlinear encoding
  const encode = (v) => v.map((xi,i) => [Math.cos(PI*xi), Math.sin(PI*xi), Math.cos(PI*xi*v[(i+1)%v.length])]);
  const ex = encode(x).flat();
  const ey = encode(y).flat();
  const dotVal = dot(ex,ey)/(Math.sqrt(dot(ex,ex))*Math.sqrt(dot(ey,ey))+1e-8);
  return Math.max(0, dotVal**2);
};

// Classical RBF kernel for comparison
const rbfKernel = (x, y, gamma=1.0) => {
  const d2 = x.reduce((s,v,i)=>s+(v-y[i])**2,0);
  return Math.exp(-gamma*d2);
};

// Generate XOR dataset (not linearly separable — quantum kernel should shine)
const genXOR = (n=40) =>
  Array.from({length:n},(_,i)=>{
    const x = Math.random()*2-1;
    const y = Math.random()*2-1;
    const label = (x*y > 0) ? 1 : -1;
    return {x, y, label, i};
  });

// VQC simulation: parameterized quantum circuit for classification
// L layers, each layer: Ry(θ) on each qubit + CNOT ring
const simVQC = (nQubits, nLayers, lr, epochs, data) => {
  let params = Array.from({length:nLayers*nQubits}, ()=>Math.random()*2*PI);
  const history = [];
  
  const predict = (x, p) => {
    // Encode x as initial rotations, then apply layers
    let angle = 0;
    for(let l=0;l<nLayers;l++){
      for(let q=0;q<nQubits;q++){
        angle += p[l*nQubits+q] * Math.cos(PI*x[q%x.length]);
      }
    }
    return sigmoid(angle * 3 - 1.5);
  };

  for(let ep=0;ep<epochs;ep++){
    let loss=0, correct=0;
    const grad = params.map(()=>0);
    
    data.forEach(d => {
      const xv = [d.x, d.y];
      const yhat = predict(xv, params);
      const ytrue = d.label > 0 ? 1 : 0;
      const err = yhat - ytrue;
      loss += (err**2);
      if((yhat>0.5) === (ytrue>0.5)) correct++;
      
      // Param-shift rule gradient estimation
      params.forEach((_,k)=>{
        const pp = [...params]; pp[k]+=PI/2;
        const pm = [...params]; pm[k]-=PI/2;
        grad[k] += err * (predict(xv,pp) - predict(xv,pm)) / 2;
      });
    });
    
    params = params.map((p,k) => p - lr * grad[k]/data.length);
    loss /= data.length;
    
    if(ep % Math.ceil(epochs/30) === 0)
      history.push({epoch:ep, loss:+loss.toFixed(4), acc:+(correct/data.length).toFixed(3)});
  }
  return {history, params, predict};
};

// ─── VQC CIRCUIT SVG ─────────────────────────────────────────────────────────
function VQCSvg({ nQubits, nLayers, params }) {
  const W=680, H=nQubits*60+50, qH=60, wireY=q=>40+q*qH;
  const colW=55, startX=70;
  const gateColor = '#4ade80';

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {Array.from({length:nQubits},(_,q)=>(
        <g key={q}>
          <text x={8} y={wireY(q)+5} style={{fontSize:'11px',fill:'#a78bfa',fontFamily:'JetBrains Mono',fontWeight:700}}>q{q}</text>
          <text x={30} y={wireY(q)+5} style={{fontSize:'10px',fill:'#334155',fontFamily:'JetBrains Mono'}}>|0⟩</text>
          <line x1={startX} y1={wireY(q)} x2={W-20} y2={wireY(q)} stroke="#1e293b" strokeWidth={2}/>
        </g>
      ))}

      {/* Encoding block */}
      <rect x={startX} y={15} width={colW-4} height={H-25} rx={3} fill="#22d3ee08" stroke="#22d3ee44" strokeDasharray="3 2"/>
      <text x={startX+colW/2-2} y={12} textAnchor="middle" style={{fontSize:'7px',fill:'#22d3ee',fontFamily:'JetBrains Mono'}}>ENCODE</text>
      {Array.from({length:nQubits},(_,q)=>(
        <g key={q}>
          <rect x={startX+4} y={wireY(q)-14} width={46} height={28} rx={3} fill="#22d3ee28" stroke="#22d3ee" strokeWidth={1.5}/>
          <text x={startX+27} y={wireY(q)+5} textAnchor="middle" style={{fontSize:'9px',fill:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>Rx(x{q})</text>
        </g>
      ))}

      {/* Variational layers */}
      {Array.from({length:nLayers},(_,l)=>{
        const lx = startX + colW + l*(colW*2+8);
        return (
          <g key={l}>
            <rect x={lx} y={15} width={colW*2+4} height={H-25} rx={3} fill={`${P[l%P.length]}06`} stroke={`${P[l%P.length]}33`} strokeDasharray="3 2"/>
            <text x={lx+colW} y={12} textAnchor="middle" style={{fontSize:'7px',fill:P[l%P.length],fontFamily:'JetBrains Mono'}}>LAYER {l+1}</text>
            {/* Ry gates */}
            {Array.from({length:nQubits},(_,q)=>{
              const angle = params ? params[l*nQubits+q] : 0;
              return (
                <g key={q}>
                  <rect x={lx+4} y={wireY(q)-14} width={46} height={28} rx={3} fill={`${P[l%P.length]}28`} stroke={P[l%P.length]} strokeWidth={1.5}/>
                  <text x={lx+27} y={wireY(q)-2} textAnchor="middle" style={{fontSize:'8px',fill:P[l%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>Ry</text>
                  <text x={lx+27} y={wireY(q)+10} textAnchor="middle" style={{fontSize:'7px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>{angle?(angle).toFixed(2):'θ'}</text>
                  {/* CNOT to next qubit */}
                  {q<nQubits-1&&<>
                    <circle cx={lx+colW+4} cy={wireY(q)} r={6} fill={`${P[l%P.length]}22`} stroke={P[l%P.length]} strokeWidth={1}/>
                    <text x={lx+colW+4} y={wireY(q)+4} textAnchor="middle" style={{fontSize:'8px',fill:P[l%P.length],fontWeight:700}}>●</text>
                    <line x1={lx+colW+4} y1={wireY(q)+6} x2={lx+colW+4} y2={wireY(q+1)-14} stroke={P[l%P.length]} strokeWidth={1.5}/>
                    <circle cx={lx+colW+4} cy={wireY(q+1)} r={8} fill="none" stroke={P[l%P.length]} strokeWidth={1.5}/>
                    <line x1={lx+colW-4} y1={wireY(q+1)} x2={lx+colW+12} y2={wireY(q+1)} stroke={P[l%P.length]} strokeWidth={1.5}/>
                  </>}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* Measure */}
      {Array.from({length:nQubits},(_,q)=>{
        const mx = startX+colW+nLayers*(colW*2+8)+8;
        return (
          <g key={q}>
            <rect x={mx} y={wireY(q)-14} width={36} height={28} rx={3} fill="#64748b22" stroke="#64748b" strokeWidth={1}/>
            <text x={mx+18} y={wireY(q)+5} textAnchor="middle" style={{fontSize:'10px',fill:'#64748b'}}>⊙</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── KERNEL HEATMAP ──────────────────────────────────────────────────────────
function KernelHeatmap({ data, kernel, title, color }) {
  const n = Math.min(8, data.length);
  const pts = data.slice(0,n);
  const cells = [];
  for(let i=0;i<n;i++) for(let j=0;j<n;j++){
    const v = kernel([pts[i].x,pts[i].y],[pts[j].x,pts[j].y]);
    cells.push({i,j,v:+v.toFixed(3)});
  }
  const cellSize=36;
  const W=n*cellSize+2, H=n*cellSize+2;
  return (
    <div>
      <div style={{fontSize:'9px',color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'4px'}}>{title}</div>
      <svg width={W} height={H} style={{display:'block',borderRadius:'4px',border:'1px solid #1e293b'}}>
        {cells.map(({i,j,v})=>(
          <rect key={`${i}-${j}`} x={j*cellSize} y={i*cellSize} width={cellSize-1} height={cellSize-1}
            fill={`rgba(${color==='#4ade80'?'74,222,128':'34,211,238'},${v.toFixed(2)})`}/>
        ))}
      </svg>
    </div>
  );
}

const ENCODING_METHODS = [
  {name:'Angle Encoding',formula:'Ry(πxᵢ)|0⟩',nQubits:'n = d',desc:'Each feature maps to a rotation angle. Simple, but only d qubits of info per shot.',color:'#22d3ee'},
  {name:'Amplitude Encoding',formula:'|ψ⟩=Σxᵢ|i⟩/‖x‖',nQubits:'n = log₂d',desc:'Exponential compression. d features in log₂d qubits. Expensive state preparation.',color:'#4ade80'},
  {name:'Basis Encoding',formula:'x → |x⟩ binary',nQubits:'n = log₂d',desc:'Binary string as computational basis state. Only for discrete/binary data.',color:'#a78bfa'},
  {name:'IQP Encoding',formula:'H⊗n·Dφ(x)·H⊗n',nQubits:'n = d',desc:'Instantaneous Quantum Polynomial circuit. Creates hard-to-simulate feature map.',color:'#fbbf24'},
];

const TABS = ['vqc','kernels','encoding','advantage','landscape'];

export default function QMLLab() {
  const [tab, setTab] = useState('vqc');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'QML Lab — quantum machine learning: variational circuits, kernels, and quantum advantage.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [nQubits, setNQubits] = useState(2);
  const [nLayers, setNLayers] = useState(2);
  const [lr, setLr] = useState(0.1);
  const [epochs, setEpochs] = useState(60);
  const [vqcResult, setVqcResult] = useState(null);
  const [vqcRunning, setVqcRunning] = useState(false);
  const [dataset] = useState(()=>genXOR(40));
  const [gamma, setGamma] = useState(1.0);
  const [kernelType, setKernelType] = useState('quantum');

  const runVQC = () => {
    setVqcRunning(true);
    setTimeout(()=>{
      const r = simVQC(nQubits, nLayers, lr, epochs, dataset);
      setVqcResult(r);
      const finalAcc = r.history.at(-1)?.acc;
      log(`VQC trained: ${nQubits}q ${nLayers}L ${epochs}ep → acc=${(finalAcc*100).toFixed(1)}%`,'ok');
      setVqcRunning(false);
    }, 80);
  };

  // Kernel scores on dataset
  const kernelScores = dataset.slice(0,20).map((d,i)=>{
    const qk = quantumKernel([d.x,d.y],[dataset[0].x,dataset[0].y]);
    const ck = rbfKernel([d.x,d.y],[dataset[0].x,dataset[0].y],gamma);
    return {i, quantum:+qk.toFixed(4), classical:+ck.toFixed(4), label:d.label};
  });

  // Barren plateau simulation
  const barrenData = Array.from({length:8},(_,n)=>{
    const nq=n+1;
    const variance = Math.exp(-0.7*nq);
    return {qubits:nq, gradVariance:+variance.toFixed(5), logVar:+Math.log10(variance+1e-6).toFixed(2)};
  });

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── VQC ── */}
      {tab==='vqc'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="VARIATIONAL QUANTUM CIRCUIT (VQC) ARCHITECTURE" accent={AC}>
            <VQCSvg nQubits={nQubits} nLayers={nLayers} params={vqcResult?.params}/>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="VQC CONFIG" accent={AC}>
              <Sl label="Qubits" value={nQubits} min={1} max={4} step={1} onChange={setNQubits} color={AC}/>
              <Sl label="Layers" value={nLayers} min={1} max={4} step={1} onChange={setNLayers} color="#22d3ee"/>
              <Sl label="Learning rate" value={lr} min={0.01} max={0.5} step={0.01} onChange={setLr} color="#fbbf24"/>
              <Sl label="Epochs" value={epochs} min={20} max={200} step={10} onChange={setEpochs} color="#a78bfa"/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px'}}>
                Params: <span style={{color:AC,fontWeight:700}}>{nQubits*nLayers}</span> · 
                Gates: <span style={{color:'#22d3ee',fontWeight:700}}>{nQubits*(nLayers+1) + (nQubits-1)*nLayers}</span>
              </div>
              <Btn onClick={runVQC} disabled={vqcRunning} color={AC} style={{width:'100%'}}>{vqcRunning?'TRAINING…':'TRAIN VQC →'}</Btn>
              {vqcResult&&<div style={{marginTop:'8px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'5px'}}>
                <Metric label="Final acc" value={`${(vqcResult.history.at(-1)?.acc*100).toFixed(1)}%`} color={AC}/>
                <Metric label="Final loss" value={vqcResult.history.at(-1)?.loss} color="#f87171"/>
              </div>}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {vqcResult?(
                <Panel title="VQC TRAINING — LOSS & ACCURACY" accent={AC}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={vqcResult.history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'epoch',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                      <YAxis yAxisId="left" tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <YAxis yAxisId="right" orientation="right" tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <Tooltip/><Legend/>
                      <Line yAxisId="left" type="monotone" dataKey="loss" stroke="#f87171" strokeWidth={2} dot={false} name="Loss"/>
                      <Line yAxisId="right" type="monotone" dataKey="acc"  stroke={AC}      strokeWidth={2} dot={false} name="Accuracy"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:200,color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and train VQC on XOR dataset</div>}
              <Panel title="PARAM-SHIFT RULE — QUANTUM GRADIENT ESTIMATION" accent="#fbbf24">
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'4px',marginBottom:'8px',borderLeft:'3px solid #fbbf24'}}>
                  <div style={{fontFamily:'JetBrains Mono',fontSize:'11px',color:'#fbbf24',marginBottom:'4px'}}>∂⟨O⟩/∂θ = ½[⟨O⟩(θ+π/2) − ⟨O⟩(θ−π/2)]</div>
                  <div style={{fontSize:'9px',color:'#64748b'}}>Exact gradient via 2 circuit evaluations per parameter</div>
                </div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>Unlike classical autodiff (backprop), quantum circuits can't differentiate analytically. The parameter-shift rule gives exact gradients by running the circuit twice with shifted parameters — a fundamental property of quantum gates.</div>
              </Panel>
            </div>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── KERNELS ── */}
      {tab==='kernels'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="KERNEL CONFIG" accent="#22d3ee">
              <Sl label="RBF gamma" value={gamma} min={0.1} max={5.0} step={0.1} onChange={setGamma} color="#22d3ee"/>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                <div style={{color:'#22d3ee',fontWeight:700,marginBottom:'4px'}}>Quantum Kernel</div>
                k(x,y) = |⟨φ(x)|φ(y)⟩|²<br/>
                Feature map φ lives in exponentially large Hilbert space.<br/><br/>
                <div style={{color:'#4ade80',fontWeight:700,marginBottom:'4px'}}>Classical RBF Kernel</div>
                k(x,y) = exp(−γ‖x−y‖²)<br/>
                Standard SVM kernel. Polynomial complexity.
              </div>
            </Panel>
            <Panel title="KERNEL VALUES vs DISTANCE TO REFERENCE POINT" accent="#22d3ee">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={kernelScores}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="i" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'data point index',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                  <Tooltip/><Legend/>
                  <Line type="monotone" dataKey="quantum"   stroke="#4ade80" strokeWidth={2} dot={false} name="Quantum kernel"/>
                  <Line type="monotone" dataKey="classical" stroke="#22d3ee" strokeWidth={2} dot={false} name="Classical RBF"/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>
          <Panel title="QUANTUM KERNEL ADVANTAGE — WHEN DOES IT HELP?" accent="#a78bfa">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {title:'Quantum-native data',desc:'Data generated by quantum systems (molecules, quantum simulations). Quantum feature maps match data structure naturally.',color:'#4ade80',verdict:'✓ Advantage'},
                {title:'High-dimensional features',desc:'2ⁿ-dimensional Hilbert space for n qubits. Exponentially more expressive than classical polynomial kernels of same size.',color:'#22d3ee',verdict:'✓ Potential'},
                {title:'Classical datasets',desc:'For most current ML datasets (MNIST, tabular, NLP), classical kernels match or beat quantum kernels with much less overhead.',color:'#f87171',verdict:'✗ No advantage yet'},
              ].map(({title,desc,color,verdict})=>(
                <div key={title} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{title}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'6px'}}>{desc}</div>
                  <div style={{fontSize:'9px',color,fontFamily:'JetBrains Mono',fontWeight:700}}>{verdict}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── ENCODING ── */}
      {tab==='encoding'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px'}}>
            {ENCODING_METHODS.map(e=>(
              <Panel key={e.name} title={e.name} accent={e.color}>
                <div style={{padding:'6px 10px',background:'var(--deep)',borderRadius:'3px',marginBottom:'8px',fontFamily:'JetBrains Mono',fontSize:'10px',color:e.color}}>{e.formula}</div>
                <div style={{fontSize:'9px',marginBottom:'6px'}}><span style={{color:'var(--t3)'}}>Qubits needed: </span><span style={{color:e.color,fontWeight:700}}>{e.nQubits}</span></div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>{e.desc}</div>
              </Panel>
            ))}
          </div>
          <Panel title="ENCODING COMPARISON — QUBITS vs EXPRESSIVITY vs COST" accent="#fbbf24">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Encoding','Qubits','Features','State prep cost','Expressivity','Best for'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['Angle','n = d','d','O(d)','Low','Simple continuous features'],
                  ['Amplitude','n = log₂d','d','O(d) (hard)','High','Large feature vectors'],
                  ['Basis','n = ⌈log₂d⌉','d (discrete)','O(1)','Low','Binary/integer data'],
                  ['IQP','n = d','d²','O(d²)','Very High','Hardness-based advantage'],
                  ['Qsample','n = d','Prob. dist.','O(n²)','High','Generative QML'],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    {row.map((cell,j)=><td key={j} style={{padding:'6px 10px',color:j===0?P[i%P.length]:'var(--t3)',fontWeight:j===0?700:400,fontFamily:j>0?'JetBrains Mono':'inherit',fontSize:'9px'}}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      )}

      {/* ── ADVANTAGE ── */}
      {tab==='advantage'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="BARREN PLATEAUS — THE TRAINING CURSE OF DEEP VQCs" accent="#f87171">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{fontSize:'10px',color:'#f87171',fontWeight:700,marginBottom:'6px'}}>Gradient Variance vs Circuit Width</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={barrenData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="qubits" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'# qubits',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                    <Tooltip/>
                    <Line type="monotone" dataKey="gradVariance" stroke="#f87171" strokeWidth={2} dot={false} name="Gradient variance"/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                <div style={{padding:'10px',background:'var(--deep)',borderRadius:'4px',borderLeft:'3px solid #f87171',fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                  <span style={{color:'#f87171',fontWeight:700}}>Barren Plateau: </span>
                  Gradient variance vanishes exponentially with circuit width: Var[∂L/∂θ] ∝ 1/2ⁿ<br/><br/>
                  Random initialization → all gradients ≈ 0 → training impossible for large circuits.
                </div>
                {[
                  {fix:'Layer-by-layer training',desc:'Train one layer at a time, then add next'},
                  {fix:'Identity block init',desc:'Initialize gates near identity transformation'},
                  {fix:'Local cost functions',desc:'Measure local instead of global observables'},
                  {fix:'Classical pre-training',desc:'Warm start parameters from classical model'},
                ].map(({fix,desc})=>(
                  <div key={fix} style={{padding:'6px 10px',background:'var(--deep)',borderRadius:'3px',borderLeft:'2px solid #4ade80',fontSize:'9px'}}>
                    <span style={{color:'#4ade80',fontWeight:700}}>{fix}: </span>
                    <span style={{color:'var(--t3)'}}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="NISQ ERA — NOISY INTERMEDIATE-SCALE QUANTUM" accent="#fbbf24">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {era:'NISQ (Now)',years:'2023–2030?',qubits:'50–1000',error:'0.1–1%',algo:'VQE, QAOA, QML (small)',color:'#fbbf24'},
                {era:'Early FTQC',years:'2030–2035?',qubits:'1M (logical)',error:'<0.001%',algo:'Small Shor, Grover, HHL',color:'#22d3ee'},
                {era:'Fault-Tolerant',years:'2035+?',qubits:'1M+ logical',error:'<0.0001%',algo:'Full Shor, large Grover, QML',color:'#4ade80'},
              ].map(({era,years,qubits,error,algo,color})=>(
                <div key={era} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'11px',color,fontWeight:700,marginBottom:'5px'}}>{era}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                    <div>Years: <span style={{color}}>{years}</span></div>
                    <div>Qubits: <span style={{color}}>{qubits}</span></div>
                    <div>Error: <span style={{color}}>{error}</span></div>
                    <div style={{marginTop:'5px',borderTop:'1px solid var(--b0)',paddingTop:'5px'}}>Algorithms: {algo}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── LANDSCAPE ── */}
      {tab==='landscape'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="QML ALGORITHM LANDSCAPE" accent={AC}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Algorithm','Type','Speedup','Qubits needed','Readiness','Notes'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[
                  ['VQE','Hybrid variational','Heuristic','10–1000 (NISQ)','Medium','Best near-term QML app'],
                  ['QAOA','Combinatorial opt','Heuristic','50–500 (NISQ)','Medium','Noisy results currently'],
                  ['Quantum SVM','Supervised','Exp (contested)','log₂N','Low','Dequantized by Tang'],
                  ['QNN','Supervised','Unknown','10–50','Low','Barren plateau problem'],
                  ['QPCA','Unsupervised','Exp (contested)','log₂N','Low','Dequantized by Tang'],
                  ['Quantum sampling','Generative','Exp (some)','50+','Medium','Boson sampling demos'],
                  ['HHL','Linear systems','Exp (caveats)','log₂N','Low','Input/output bottleneck'],
                  ['Quantum walk','Graph/search','Polynomial','log₂N','Medium','More robust advantage'],
                ].map((row,i)=>(
                  <tr key={row[0]} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    {row.map((cell,j)=>(
                      <td key={j} style={{padding:'6px 10px',
                        color:j===0?P[i%P.length]:j===4?({'High':'#4ade80','Medium':'#fbbf24','Low':'#f87171'}[cell]||'var(--t3)'):'var(--t3)',
                        fontWeight:j===0?700:400,fontFamily:j>0&&j<5?'JetBrains Mono':'inherit',fontSize:'9px'}}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="DEQUANTIZATION — THE HONEST PICTURE" accent="#f87171">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Ewin Tang (2019) showed quantum-inspired classical algorithms can match many claimed quantum ML speedups, using classical sampling techniques.
              </div>
              {['Quantum PCA → dequantized by Tang 2019','Quantum recommendation systems → dequantized','Quantum SVM (log speedup) → classical match','HHL linear systems → input/output kills speedup'].map((s,i)=>(
                <div key={i} style={{padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',marginBottom:'3px',borderLeft:'2px solid #f87171',fontSize:'9px',color:'var(--t3)'}}>{s}</div>
              ))}
              <div style={{marginTop:'8px',padding:'8px',background:'#4ade8010',borderRadius:'3px',border:'1px solid #4ade8030',fontSize:'9px',color:'#4ade80',lineHeight:1.7}}>
                Robust advantages remain in: simulation of quantum systems, Shor factoring, quantum error correction, some sampling tasks.
              </div>
            </Panel>
            <Panel title="QML FRAMEWORKS TODAY" accent="#22d3ee">
              {[
                {name:'PennyLane',org:'Xanadu',use:'QML, VQC, hybrid, autodiff',color:'#22d3ee'},
                {name:'Qiskit ML',org:'IBM',use:'QSVM, VQC, Qiskit integration',color:'#4ade80'},
                {name:'TensorFlow Quantum',org:'Google',use:'Hybrid TF + Cirq circuits',color:'#fbbf24'},
                {name:'Amazon Braket',org:'AWS',use:'Multi-backend, NISQ devices',color:'#fb923c'},
                {name:'Cirq',org:'Google',use:'Low-level gate construction',color:'#a78bfa'},
              ].map(({name,org,use,color})=>(
                <div key={name} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'3px',marginBottom:'4px',borderLeft:`2px solid ${color}`}}>
                  <span style={{fontSize:'9px',color,fontWeight:700,minWidth:120}}>{name}</span>
                  <span style={{fontSize:'8px',color:'#64748b',minWidth:50}}>{org}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)'}}>{use}</span>
                </div>
              ))}
            </Panel>
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
